import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { db, storage } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, doc, getDoc, addDoc, updateDoc, serverTimestamp, getDocs
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  Box, Container, Typography, Paper, Button, TextField, MenuItem,
  CircularProgress, Alert, Chip, LinearProgress, Divider, IconButton, List, ListItem, ListItemText,
  Switch, FormControlLabel, Collapse, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import SubwayIcon from '@mui/icons-material/Subway';
import TramIcon from '@mui/icons-material/Tram';
import TrainIcon from '@mui/icons-material/Train';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LagbasLogo from '../components/LagbasLogo';
import { searchLocation } from '../utils/departureApi';
import { PRESET_BACKGROUNDS } from '../constants/castNConstants';
import { SlideRenderer } from './CastNDisplay';
import { ZONE_OPTIONS } from './CastNCasts';
import { authFetch } from '../utils/authFetch';

export { PRESET_BACKGROUNDS };

const TYPE_LABELS = {
  text: 'Text & Bild',
  media: 'Media (bild/video/pdf)',
  image_url: 'Bild via URL',
  video: 'Video / YouTube',
  weather: 'Väder (SMHI)',
  weather_map: 'Väderkarta (Windy)',
  traffic: 'Avgångar',
  rss: 'RSS-nyheter',
  url: 'Webbsida / Kalender',
  world_clock: 'Världsklocka',
  menu: 'Veckans meny',
  lagbasn_reference: 'Referensprojekt',
  lagbasn_leads: 'Aktiva anbud',
  lagbasn_wins: 'Vunna affärer',
  date_tearoff: 'Datumlapp',
  emoji: 'Emoji / Ikon',
  google_calendar: 'Google Kalender',
  ms_calendar: 'Microsoft Kalender',
  power_bi: 'Power BI',
  sharepoint: 'SharePoint',
  social_facebook: 'Facebook',
  social_instagram: 'Instagram',
  social_linkedin: 'LinkedIn',
  qr_display: 'QR-kod – dela bildspel',
  birthday:   'Födelsedagshälsning',
};

const ACCEPTED_FILES = '.jpg,.jpeg,.png,.gif,.webp,.svg,.pdf,.mp4,.mov,.webm';

function TextForm({ content, onChange }) {
  const { currentUser } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState(content.image ? 'image' : content.bgPresetId ? 'preset' : 'image');
  const [unsplashQuery, setUnsplashQuery] = useState('');
  const [unsplashResults, setUnsplashResults] = useState([]);
  const [unsplashLoading, setUnsplashLoading] = useState(false);
  const [unsplashError, setUnsplashError] = useState('');

  async function searchUnsplash() {
    if (!unsplashQuery.trim()) return;
    setUnsplashLoading(true);
    setUnsplashError('');
    try {
      const res = await authFetch(`/.netlify/functions/unsplash?q=${encodeURIComponent(unsplashQuery)}&per_page=12`);
      const data = await res.json();
      if (data.error) {
        setUnsplashError(data.error.includes('not configured')
          ? 'Unsplash-nyckel ej konfigurerad. Kontakta administratören.'
          : `Fel: ${data.error}`);
        setUnsplashResults([]);
      } else {
        setUnsplashResults(data.results || []);
      }
    } catch(e) {
      setUnsplashError('Kunde inte nå Unsplash-tjänsten.');
      console.error(e);
    }
    finally { setUnsplashLoading(false); }
  }

  async function handleImageFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const path = `slides/text-bg/${Date.now()}_${file.name}`;
      const sRef = storageRef(storage, path);
      await uploadBytes(sRef, file, { customMetadata: { uploadedBy: currentUser?.uid || '' } });
      const url = await getDownloadURL(sRef);
      onChange({ ...content, image: url, bgPresetId: null });
    } catch (e) { console.error(e); }
    finally { setUploading(false); }
  }

  return (
    <Box sx={{ display:'flex', flexDirection:'column', gap: 2 }}>
      <TextField label="Rubrik" value={content.title || ''} onChange={e => onChange({ ...content, title: e.target.value })} fullWidth />
      <TextField label="Brödtext" value={content.body || ''} onChange={e => onChange({ ...content, body: e.target.value })}
        fullWidth multiline rows={4} />
      <TextField select label="Layout" value={content.layout || 'text-only'} onChange={e => onChange({ ...content, layout: e.target.value })} fullWidth>
        <MenuItem value="text-only">Bara text</MenuItem>
        <MenuItem value="image-right">Text vänster, bild höger</MenuItem>
        <MenuItem value="image-left">Bild vänster, text höger</MenuItem>
        <MenuItem value="bg-only">Bild som bakgrund (text över)</MenuItem>
      </TextField>

      {/* Bakgrund / Bild */}
      <Box>
        <Box sx={{ display:'flex', gap: 1, mb: 1.5, flexWrap:'wrap' }}>
          <Button size="small" variant={tab==='image' ? 'contained':'outlined'} onClick={() => setTab('image')}
            sx={tab==='image' ? { bgcolor:'#FF6B00','&:hover':{bgcolor:'#e55a00'} } : { borderColor:'#FF6B00',color:'#FF6B00' }}>
            Ladda upp bild
          </Button>
          <Button size="small" variant={tab==='unsplash' ? 'contained':'outlined'} startIcon={<SearchIcon />}
            onClick={() => setTab('unsplash')}
            sx={tab==='unsplash' ? { bgcolor:'#FF6B00','&:hover':{bgcolor:'#e55a00'} } : { borderColor:'#FF6B00',color:'#FF6B00' }}>
            Unsplash
          </Button>
          <Button size="small" variant={tab==='preset' ? 'contained':'outlined'} startIcon={<WallpaperIcon />}
            onClick={() => setTab('preset')}
            sx={tab==='preset' ? { bgcolor:'#FF6B00','&:hover':{bgcolor:'#e55a00'} } : { borderColor:'#FF6B00',color:'#FF6B00' }}>
            Bakgrundsfärg
          </Button>
        </Box>

        {tab === 'image' && (
          <Box sx={{ display:'flex', flexDirection:'column', gap: 1.5 }}>
            {content.image && (
              <Box sx={{ position:'relative', borderRadius: 1, overflow:'hidden', maxHeight: 120 }}>
                <img src={content.image} alt="bg" style={{ width:'100%', height:120, objectFit:'cover' }} />
                <IconButton size="small" sx={{ position:'absolute', top:4, right:4, bgcolor:'rgba(0,0,0,0.6)', color:'#fff' }}
                  onClick={() => { const { image: _i, ...rest } = content; onChange(rest); }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            <Box sx={{ border:'2px dashed #ddd', borderRadius: 1, p: 2, textAlign:'center' }}>
              <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}
                sx={{ borderColor:'#FF6B00', color:'#FF6B00' }}>
                {content.image ? 'Byt bild' : 'Välj bild (JPG/PNG/WebP/GIF/SVG)'}
                <input type="file" accept=".jpg,.jpeg,.png,.gif,.webp,.svg" hidden onChange={handleImageFile} />
              </Button>
            </Box>
            {uploading && <LinearProgress sx={{ '& .MuiLinearProgress-bar':{ bgcolor:'#FF6B00' } }} />}

            {/* Bildeffekter / toning */}
            {content.image && (
              <Box>
                <Typography variant="caption" sx={{ color:'#888', mb: 0.5, display:'block' }}>Bildeffekt / toning</Typography>
                <Box sx={{ display:'flex', flexWrap:'wrap', gap: 1 }}>
                  {[
                    { id: 'none',      label: 'Ingen',       overlay: '' },
                    { id: 'dark',      label: 'Mörk',        overlay: 'rgba(0,0,0,0.5)' },
                    { id: 'darker',    label: 'Mörkare',     overlay: 'rgba(0,0,0,0.75)' },
                    { id: 'orange',    label: 'Orange',      overlay: 'rgba(255,107,0,0.45)' },
                    { id: 'blue',      label: 'Blå',         overlay: 'rgba(21,101,192,0.5)' },
                    { id: 'green',     label: 'Grön',        overlay: 'rgba(6,78,59,0.5)' },
                    { id: 'sepia',     label: 'Sepia',       overlay: 'rgba(120,80,20,0.4)' },
                    { id: 'bw',        label: 'Svartvit',    overlay: 'grayscale' },
                  ].map(ef => {
                    const active = (content.imgEffect || 'none') === ef.id;
                    return (
                      <Box key={ef.id} onClick={() => onChange({ ...content, imgEffect: ef.id })}
                        sx={{ px: 1.5, py: 0.5, borderRadius: 1, cursor:'pointer', fontSize:'0.75rem',
                          fontWeight: active ? 700 : 400,
                          border: active ? '2px solid #FF6B00' : '2px solid #e0e0e0',
                          bgcolor: active ? 'rgba(255,107,0,0.08)' : 'transparent',
                          '&:hover':{ borderColor:'#FF6B00' } }}>
                        {ef.label}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {tab === 'unsplash' && (
          <Box sx={{ display:'flex', flexDirection:'column', gap: 1.5 }}>
            <Box sx={{ display:'flex', gap: 1 }}>
              <TextField
                size="small" fullWidth
                label="Sök på Unsplash"
                value={unsplashQuery}
                onChange={e => setUnsplashQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && searchUnsplash()}
                placeholder="t.ex. office, nature, city..."
              />
              <Button variant="outlined" onClick={searchUnsplash} disabled={unsplashLoading}
                startIcon={unsplashLoading ? <CircularProgress size={14} /> : <SearchIcon />}
                sx={{ whiteSpace:'nowrap', borderColor:'#FF6B00', color:'#FF6B00' }}>
                Sök
              </Button>
            </Box>
            {content.image && content.unsplashCredit && (
              <Typography variant="caption" sx={{ color:'#888' }}>
                Vald bild: foto av {content.unsplashCredit} (Unsplash)
              </Typography>
            )}
            {unsplashResults.length > 0 && (
              <Box sx={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap: 0.75 }}>
                {unsplashResults.map(img => (
                  <Box key={img.id} onClick={() => onChange({ ...content, image: img.regular, bgPresetId: null, unsplashCredit: img.credit })}
                    sx={{ position:'relative', aspectRatio:'16/9', borderRadius: 1, overflow:'hidden', cursor:'pointer',
                      border: content.image === img.regular ? '3px solid #FF6B00' : '3px solid transparent',
                      '&:hover':{ transform:'scale(1.03)', zIndex:1 }, transition:'all 0.15s' }}>
                    <img src={img.thumb} alt={img.alt} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                    {content.image === img.regular && (
                      <CheckCircleIcon sx={{ position:'absolute', top:4, right:4, color:'#FF6B00', bgcolor:'white', borderRadius:'50%', fontSize:18 }} />
                    )}
                  </Box>
                ))}
              </Box>
            )}
            {unsplashError && (
              <Alert severity="warning" sx={{ fontSize:'0.8rem' }}>{unsplashError}</Alert>
            )}
            {!unsplashResults.length && !unsplashLoading && !unsplashError && (
              <Typography variant="caption" sx={{ color:'#aaa', textAlign:'center', pt: 1 }}>
                Sök efter gratis licensfria bilder från Unsplash
              </Typography>
            )}
          </Box>
        )}

        {tab === 'preset' && (
          <Box sx={{ display:'flex', flexWrap:'wrap', gap: 1 }}>
            {PRESET_BACKGROUNDS.map(bg => (
              <Box key={bg.id} onClick={() => onChange({ ...content, bgPresetId: bg.id, image: null })}
                sx={{ position:'relative', width: 80, height: 50, borderRadius: 1, cursor:'pointer', background: bg.css,
                  border: content.bgPresetId === bg.id ? '3px solid #FF6B00' : '3px solid transparent',
                  boxShadow: content.bgPresetId === bg.id ? '0 0 0 2px #FF6B00' : 'none',
                  transition:'all 0.15s', '&:hover':{ transform:'scale(1.05)' } }}>
                {content.bgPresetId === bg.id && (
                  <CheckCircleIcon sx={{ position:'absolute', top:2, right:2, fontSize:14, color:'#fff', filter:'drop-shadow(0 1px 2px #000)' }} />
                )}
                <Typography sx={{ position:'absolute', bottom:2, left:4, fontSize:'0.55rem', color:'rgba(255,255,255,0.8)', fontWeight:600,
                  textShadow:'0 1px 2px rgba(0,0,0,0.8)' }}>
                  {bg.label}
                </Typography>
              </Box>
            ))}
            {content.bgPresetId && (
              <Button size="small" onClick={() => { const { bgPresetId: _b, ...rest } = content; onChange(rest); }}
                sx={{ color:'#999', alignSelf:'center' }}>
                Ta bort
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// Shared display mode options (same as PinToMind)
const DISPLAY_MODES = [
  {
    value: 'zone-contain',
    label: 'Centrerad i zon',
    sub: 'visa hela bilden',
    icon: (
      <svg width="48" height="32" viewBox="0 0 48 32">
        <rect x="0" y="0" width="48" height="32" fill="#e0e0e0" rx="2"/>
        <rect x="10" y="6" width="28" height="20" fill="white" stroke="#ccc" rx="1"/>
        <rect x="16" y="9" width="16" height="14" fill="#bbb" rx="1"/>
      </svg>
    ),
  },
  {
    value: 'zone-cover',
    label: 'Fylla zon',
    sub: 'beskär bilden',
    icon: (
      <svg width="48" height="32" viewBox="0 0 48 32">
        <rect x="0" y="0" width="48" height="32" fill="#e0e0e0" rx="2"/>
        <rect x="10" y="6" width="28" height="20" fill="#bbb" rx="1"/>
      </svg>
    ),
  },
  {
    value: 'fullscreen-contain',
    label: 'Fylla skärmen',
    sub: 'visa hela bilden',
    breakout: true,
    icon: (
      <svg width="48" height="32" viewBox="0 0 48 32">
        <rect x="0" y="0" width="48" height="32" fill="#bbb" rx="2"/>
        <rect x="6" y="6" width="36" height="20" fill="white" stroke="#aaa" rx="1"/>
      </svg>
    ),
  },
  {
    value: 'fullscreen-cover',
    label: 'Fylla skärmen',
    sub: 'beskär bilden',
    breakout: true,
    icon: (
      <svg width="48" height="32" viewBox="0 0 48 32">
        <rect x="0" y="0" width="48" height="32" fill="#999" rx="2"/>
      </svg>
    ),
  },
  {
    value: 'collage',
    label: 'Collage',
    sub: 'alla bilder i grid',
    icon: (
      <svg width="48" height="32" viewBox="0 0 48 32">
        <rect x="0" y="0" width="48" height="32" fill="#e0e0e0" rx="2"/>
        <rect x="2" y="2" width="21" height="13" fill="#bbb" rx="1"/>
        <rect x="25" y="2" width="21" height="13" fill="#aaa" rx="1"/>
        <rect x="2" y="17" width="21" height="13" fill="#aaa" rx="1"/>
        <rect x="25" y="17" width="21" height="13" fill="#bbb" rx="1"/>
      </svg>
    ),
  },
];

function DisplayModeSelector({ value = 'zone-cover', onChange }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color:'#666', mb: 1, display:'block', fontWeight:600 }}>
        Visningsläge
      </Typography>
      <Box sx={{ display:'flex', flexWrap:'wrap', gap: 1 }}>
        {DISPLAY_MODES.map(m => {
          const active = value === m.value;
          return (
            <Box key={m.value} onClick={() => onChange(m.value)}
              sx={{ border: active ? '2px solid #FF6B00' : '2px solid #e0e0e0',
                borderRadius: 1.5, p: 1, cursor:'pointer', textAlign:'center', minWidth: 80,
                bgcolor: active ? 'rgba(255,107,0,0.06)' : 'white',
                '&:hover':{ borderColor:'#FF6B00' }, transition:'all 0.15s' }}>
              {m.icon}
              <Typography sx={{ fontSize:'0.7rem', fontWeight: active ? 700 : 400, mt: 0.5, lineHeight:1.2 }}>
                {m.label}
              </Typography>
              <Typography sx={{ fontSize:'0.6rem', color:'#888', lineHeight:1.2 }}>
                {m.sub}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function MediaForm({ content, onChange }) {
  const { currentUser } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  async function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setUploadError('');
    try {
      const path = `slides/${Date.now()}_${file.name}`;
      const sRef = storageRef(storage, path);
      // NO compression or resize – raw File object
      await uploadBytes(sRef, file, {
        customMetadata: {
          originalName: file.name,
          originalSize: String(file.size),
          uploadedBy: currentUser?.uid || '',
        }
      });
      const url = await getDownloadURL(sRef);
      const ext = file.name.split('.').pop().toLowerCase();
      onChange({
        ...content,
        url,
        fileType: ext,
        originalName: file.name,
        originalSize: file.size,
      });
    } catch(e) { setUploadError(e.message); }
    finally { setUploading(false); }
  }

  return (
    <Box sx={{ display:'flex', flexDirection:'column', gap: 2 }}>
      <Box sx={{ border:'2px dashed #ddd', borderRadius: 2, p: 3, textAlign:'center' }}>
        <UploadFileIcon sx={{ fontSize: 48, color:'#ccc', mb: 1 }} />
        <Typography sx={{ color:'#666', mb: 1 }}>JPG, PNG, GIF, WebP, SVG, PDF, MP4, MOV, WebM</Typography>
        <Typography variant="caption" sx={{ color:'#aaa', display:'block', mb: 2 }}>Max kommunicerad storlek: 100MB</Typography>
        <Button variant="outlined" component="label"
          sx={{ borderColor:'#FF6B00', color:'#FF6B00' }}>
          Välj fil
          <input type="file" accept={ACCEPTED_FILES} hidden onChange={handleFile} />
        </Button>
      </Box>
      {uploading && <LinearProgress sx={{ '& .MuiLinearProgress-bar':{ bgcolor:'#FF6B00' } }} />}
      {uploadError && <Alert severity="error">{uploadError}</Alert>}
      {content.url && (
        <Alert severity="success">
          Uppladdad: {content.originalName || 'fil'} ({content.fileType?.toUpperCase()})
        </Alert>
      )}
      {content.url && !['mp4','mov','webm','pdf'].some(e => (content.fileType || '').includes(e)) && (
        <DisplayModeSelector
          value={content.displayMode || 'zone-cover'}
          onChange={v => onChange({ ...content, displayMode: v })}
        />
      )}
    </Box>
  );
}

function AlbumForm({ content, onChange }) {
  const { currentUser } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const images = content.images || [];

  async function handleFiles(e) {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true); setUploadProgress(0);
    const urls = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const path = `slides/albums/${Date.now()}_${file.name}`;
      const sRef = storageRef(storage, path);
      await uploadBytes(sRef, file, { customMetadata: { uploadedBy: currentUser?.uid || '' } });
      const url = await getDownloadURL(sRef);
      urls.push(url);
      setUploadProgress(Math.round(((i + 1) / files.length) * 100));
    }
    onChange({ ...content, images: [...images, ...urls] });
    setUploading(false);
    e.target.value = '';
  }

  function removeImage(idx) {
    onChange({ ...content, images: images.filter((_, i) => i !== idx) });
  }

  function moveImage(from, to) {
    const arr = [...images];
    const [item] = arr.splice(from, 1);
    arr.splice(to, 0, item);
    onChange({ ...content, images: arr });
  }

  const totalDuration = images.length * (content.imageDuration || 5);

  return (
    <Box sx={{ display:'flex', flexDirection:'column', gap: 2.5 }}>
      {/* Upload area */}
      <Box sx={{ border:'2px dashed #ddd', borderRadius: 2, p: 3, textAlign:'center' }}>
        <Typography sx={{ color:'#666', mb: 1, fontWeight:600 }}>Lägg till bilder</Typography>
        <Typography variant="caption" sx={{ color:'#aaa', display:'block', mb: 2 }}>
          Välj en eller flera bilder (JPG, PNG, WebP, GIF)
        </Typography>
        <Button variant="outlined" component="label"
          disabled={uploading}
          sx={{ borderColor:'#FF6B00', color:'#FF6B00', '&:hover':{ bgcolor:'rgba(255,107,0,0.05)' } }}>
          {uploading ? `Laddar upp… ${uploadProgress}%` : 'Välj bilder'}
          <input type="file" accept="image/*" multiple hidden onChange={handleFiles} />
        </Button>
      </Box>
      {uploading && <LinearProgress variant="determinate" value={uploadProgress} sx={{ '& .MuiLinearProgress-bar':{ bgcolor:'#FF6B00' } }} />}

      {/* Image grid */}
      {images.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ color:'#888', mb:1, display:'block' }}>
            {images.length} bilder · Total visningstid: {totalDuration}s
            {' '}(dra för att ändra ordning)
          </Typography>
          <Box sx={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(120px, 1fr))', gap: 1 }}>
            {images.map((img, idx) => (
              <Box key={idx} sx={{ position:'relative', aspectRatio:'16/9', borderRadius:1, overflow:'hidden',
                border:'2px solid #eee', bgcolor:'#f5f5f5',
                '&:hover .del-btn':{ opacity:1 } }}>
                <img src={img} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                {/* Position badge */}
                <Box sx={{ position:'absolute', top:4, left:4, bgcolor:'rgba(0,0,0,0.6)',
                  color:'white', fontSize:'0.6rem', fontWeight:700, px:0.75, py:0.25, borderRadius:1 }}>
                  {idx + 1}
                </Box>
                {/* Delete */}
                <Box className="del-btn" onClick={() => removeImage(idx)}
                  sx={{ position:'absolute', top:4, right:4, opacity:0, transition:'opacity 0.15s',
                    bgcolor:'rgba(220,38,38,0.85)', borderRadius:'50%', width:22, height:22,
                    display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'white', fontSize:'0.8rem' }}>
                  ×
                </Box>
                {/* Move left/right */}
                <Box sx={{ position:'absolute', bottom:0, left:0, right:0, display:'flex', justifyContent:'space-between', px:0.5, py:0.25 }}>
                  {idx > 0 && (
                    <Box onClick={() => moveImage(idx, idx - 1)}
                      sx={{ bgcolor:'rgba(0,0,0,0.55)', color:'white', borderRadius:1, px:0.5, fontSize:'0.65rem', cursor:'pointer' }}>←</Box>
                  )}
                  {idx < images.length - 1 && (
                    <Box onClick={() => moveImage(idx, idx + 1)} sx={{ ml:'auto',
                      bgcolor:'rgba(0,0,0,0.55)', color:'white', borderRadius:1, px:0.5, fontSize:'0.65rem', cursor:'pointer' }}>→</Box>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Display mode */}
      <DisplayModeSelector
        value={content.displayMode || 'zone-cover'}
        onChange={v => onChange({ ...content, displayMode: v })}
      />

      {/* Settings */}
      <Box sx={{ display:'flex', flexWrap:'wrap', gap: 2 }}>
        {(content.displayMode || 'zone-cover') !== 'collage' && (
          <TextField
            label="Tid per bild (sekunder)"
            type="number" size="small"
            value={content.imageDuration || 5}
            onChange={e => onChange({ ...content, imageDuration: Math.max(1, Number(e.target.value)) })}
            inputProps={{ min:1, max:60 }}
            sx={{ width: 200 }}
            helperText={`Total: ${totalDuration}s för ${images.length} bilder`}
          />
        )}
      </Box>
      <TextField
        label="Rubrik (valfri overlay)"
        value={content.title || ''}
        onChange={e => onChange({ ...content, title: e.target.value })}
        fullWidth size="small"
        helperText="Visas längst ned på albumet om du aktiverar titel"
      />
      <Box sx={{ display:'flex', alignItems:'center', gap: 1 }}>
        <input type="checkbox" id="showTitle" checked={!!content.showTitle}
          onChange={e => onChange({ ...content, showTitle: e.target.checked })} />
        <label htmlFor="showTitle" style={{ fontSize:'0.9rem', color:'#444', cursor:'pointer' }}>
          Visa rubrik på skärmen
        </label>
      </Box>
    </Box>
  );
}

function ImageUrlForm({ content, onChange }) {
  return (
    <TextField label="Bild-URL" value={content.url || ''} onChange={e => onChange({ ...content, url: e.target.value })} fullWidth />
  );
}

function VideoForm({ content, onChange }) {
  return (
    <Box sx={{ display:'flex', flexDirection:'column', gap: 2 }}>
      <TextField label="Video-URL eller YouTube-länk" value={content.url || ''}
        onChange={e => onChange({ ...content, url: e.target.value })} fullWidth
        helperText="YouTube-URL konverteras automatiskt till embed" />
    </Box>
  );
}

function GeoSearch({ label, onSelect }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const search = async () => {
    if (!q.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q.trim())}&format=json&limit=6&accept-language=sv`);
      const data = await res.json();
      setResults(data.map(r => ({
        name: r.display_name.split(',').slice(0, 2).join(', '),
        lat: parseFloat(r.lat),
        lon: parseFloat(r.lon),
      })));
    } catch (e) { console.error(e); }
    finally { setSearching(false); }
  };

  return (
    <Box sx={{ display:'flex', flexDirection:'column', gap: 1 }}>
      <Box sx={{ display:'flex', gap: 1 }}>
        <TextField label={label} value={q} onChange={e => setQ(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && search()} fullWidth size="small" />
        <Button variant="outlined" onClick={search} disabled={searching} startIcon={<SearchIcon />}
          sx={{ whiteSpace:'nowrap', borderColor:'#FF6B00', color:'#FF6B00' }}>
          {searching ? <CircularProgress size={16} /> : 'Sök'}
        </Button>
      </Box>
      {results.length > 0 && (
        <Paper variant="outlined" sx={{ maxHeight: 180, overflowY:'auto' }}>
          <List dense>
            {results.map((r, i) => (
              <ListItem key={i} sx={{ cursor:'pointer','&:hover':{ bgcolor:'#f5f5f5' } }}
                onClick={() => { onSelect(r); setResults([]); setQ(''); }}>
                <ListItemText primary={r.name} secondary={`${r.lat.toFixed(4)}, ${r.lon.toFixed(4)}`} />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
}

function WeatherForm({ content, onChange }) {
  return (
    <Box sx={{ display:'flex', flexDirection:'column', gap: 2 }}>
      <GeoSearch
        label="Sök plats / stad"
        onSelect={r => onChange({ ...content, city: r.name.split(',')[0].trim(), lat: r.lat, lon: r.lon })}
      />
      <Box sx={{ display:'flex', gap: 2 }}>
        <TextField label="Visningsnamn" value={content.city || ''} onChange={e => onChange({ ...content, city: e.target.value })} fullWidth size="small" />
        <TextField label="Lat" value={content.lat ?? 59.33} onChange={e => onChange({ ...content, lat: parseFloat(e.target.value) || e.target.value })} fullWidth size="small" />
        <TextField label="Lon" value={content.lon ?? 18.07} onChange={e => onChange({ ...content, lon: parseFloat(e.target.value) || e.target.value })} fullWidth size="small" />
      </Box>
      <Typography variant="caption" color="text.secondary">
        Väder hämtas från SMHI (täcker Sverige). För internationella platser används SMHI:s närmaste punkt.
      </Typography>
    </Box>
  );
}

function WeatherMapForm({ content, onChange }) {
  return (
    <Box sx={{ display:'flex', flexDirection:'column', gap: 2 }}>
      <GeoSearch
        label="Centrera karta på plats"
        onSelect={r => onChange({ ...content, lat: r.lat, lon: r.lon })}
      />
      <Box sx={{ display:'flex', gap: 2 }}>
        <TextField label="Lat" value={content.lat || '59.33'} onChange={e => onChange({ ...content, lat: e.target.value })} fullWidth size="small" />
        <TextField label="Lon" value={content.lon || '18.07'} onChange={e => onChange({ ...content, lon: e.target.value })} fullWidth size="small" />
        <TextField label="Zoom" value={content.zoom || '5'} onChange={e => onChange({ ...content, zoom: e.target.value })} fullWidth size="small" />
      </Box>
      <TextField select label="Lager" value={content.layer || 'wind'} onChange={e => onChange({ ...content, layer: e.target.value })} fullWidth>
        <MenuItem value="wind">Vind</MenuItem>
        <MenuItem value="rain">Regn</MenuItem>
        <MenuItem value="temp">Temperatur</MenuItem>
        <MenuItem value="clouds">Moln</MenuItem>
        <MenuItem value="pressure">Lufttryck</MenuItem>
      </TextField>
    </Box>
  );
}

function getStopIcon(icon) {
  if (icon === 'metro') return <SubwayIcon sx={{ fontSize: '1rem', color: '#94a3b8' }} />;
  if (icon === 'tram') return <TramIcon sx={{ fontSize: '1rem', color: '#94a3b8' }} />;
  if (icon === 'train') return <TrainIcon sx={{ fontSize: '1rem', color: '#94a3b8' }} />;
  return <DirectionsBusIcon sx={{ fontSize: '1rem', color: '#94a3b8' }} />;
}

function iconFromCatOut(catOut) {
  // ResRobot returns products as a numeric bitmask: 8=metro, 16=tram, 1|2|4=train, 32=bus
  if (typeof catOut === 'number') {
    if (catOut & 8)  return 'metro';
    if (catOut & 16) return 'tram';
    if (catOut & 7)  return 'train'; // bits 1+2+4
    return 'bus';
  }
  const c = String(catOut || '').toUpperCase();
  if (c.includes('METRO') || c.includes('TUNNELBANA') || c === 'U') return 'metro';
  if (c.includes('TRAM') || c.includes('SPÅRVÄG') || c === 'S') return 'tram';
  if (c.includes('PENDEL') || c.includes('TRAIN') || c.includes('TÅG') || c === 'J') return 'train';
  return 'bus';
}

function TrafficForm({ content, onChange }) {
  const stops = content.stops || [];
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const doSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const res = await searchLocation(query.trim());
      setResults(res || []);
    } catch (e) {
      console.error(e);
    } finally {
      setSearching(false);
    }
  };

  const addStop = (loc) => {
    const icon = iconFromCatOut(loc.products || loc.catOut || '');
    const newStop = { id: loc.extId || loc.id, name: loc.name, icon };
    if (stops.find(s => s.id === newStop.id)) return;
    onChange({ ...content, stops: [...stops, newStop] });
    setResults([]);
    setQuery('');
  };

  const removeStop = (id) => onChange({ ...content, stops: stops.filter(s => s.id !== id) });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="subtitle2" color="text.secondary">
        Sök och lägg till hållplatser – avgångar visas i realtid på skärmen
      </Typography>

      {/* Tillagda hållplatser */}
      {stops.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {stops.map(s => (
            <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 1,
              bgcolor: '#f5f5f5', borderRadius: 1, px: 1.5, py: 0.75 }}>
              {getStopIcon(s.icon)}
              <Typography variant="body2" sx={{ flex: 1 }}>{s.name}</Typography>
              <Typography variant="caption" sx={{ color: '#aaa', mr: 1 }}>{s.id}</Typography>
              <IconButton size="small" onClick={() => removeStop(s.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {/* Sökfält */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          label="Sök hållplats"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && doSearch()}
          fullWidth
          size="small"
        />
        <Button variant="outlined" onClick={doSearch} disabled={searching} startIcon={<SearchIcon />} sx={{ whiteSpace: 'nowrap' }}>
          {searching ? <CircularProgress size={16} /> : 'Sök'}
        </Button>
      </Box>

      {/* Sökresultat */}
      {results.length > 0 && (
        <Paper variant="outlined" sx={{ maxHeight: 220, overflowY: 'auto' }}>
          {results.slice(0, 12).map((loc, idx) => (
            <Box
              key={loc.extId || loc.id || idx}
              onClick={() => addStop(loc)}
              sx={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                px: 2, py: 1, cursor:'pointer', borderBottom:'1px solid #f0f0f0',
                '&:last-child':{ borderBottom:'none' },
                '&:hover':{ bgcolor:'rgba(255,107,0,0.07)' } }}
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>{loc.name}</Typography>
                <Typography variant="caption" sx={{ color:'#aaa' }}>ID: {loc.extId || loc.id}</Typography>
              </Box>
              <AddIcon sx={{ fontSize:18, color:'#FF6B00', flexShrink:0 }} />
            </Box>
          ))}
        </Paper>
      )}

      <TextField
        label="Titel (valfri, visas som rubrik)"
        value={content.title || ''}
        onChange={e => onChange({ ...content, title: e.target.value })}
        fullWidth
        size="small"
        helperText="Lämna tom för att visa hållplatsnamnen som rubriker"
      />
    </Box>
  );
}

const RSS_CATALOG = [
  { cat: '🇸🇪 Svenska nyheter', feeds: [
    { title: 'SVT Nyheter – Senaste', url: 'https://www.svt.se/nyheter/rss.xml' },
    { title: 'SR Ekot', url: 'https://api.sr.se/api/rss/program/4540?format=1' },
    { title: 'Aftonbladet – Senaste nytt', url: 'https://rss.aftonbladet.se/rss2/small/pages/sections/senastenytt/' },
    { title: 'Expressen', url: 'https://feeds.expressen.se/nyheter/' },
    { title: 'Dagens Industri', url: 'https://digital.di.se/rss' },
    { title: 'Computer Sweden', url: 'https://computersweden.se/feed/' },
  ]},
  { cat: '🌍 Internationellt', feeds: [
    { title: 'BBC World News', url: 'http://feeds.bbci.co.uk/news/world/rss.xml' },
    { title: 'Reuters – Top News', url: 'https://feeds.reuters.com/reuters/topNews' },
    { title: 'The Guardian – World', url: 'https://www.theguardian.com/world/rss' },
    { title: 'AP News', url: 'https://rsshub.app/apnews/topics/apf-topnews' },
  ]},
  { cat: '💻 Tech', feeds: [
    { title: 'TechCrunch', url: 'https://techcrunch.com/feed/' },
    { title: 'Wired', url: 'https://www.wired.com/feed/rss' },
    { title: 'The Verge', url: 'https://www.theverge.com/rss/index.xml' },
    { title: 'Hacker News – Top', url: 'https://hnrss.org/frontpage' },
    { title: 'Ars Technica', url: 'http://feeds.arstechnica.com/arstechnica/index' },
  ]},
  { cat: '💼 Ekonomi', feeds: [
    { title: 'Nasdaq – Tech News', url: 'https://feeds.nasdaq.com/nasdaq/rss/newsfeeds.aspx?category=technologyNews' },
    { title: 'Bloomberg Technology', url: 'https://feeds.bloomberg.com/technology/news.rss' },
  ]},
  { cat: '🌤 Väder & Natur', feeds: [
    { title: 'SMHI – Nyheter', url: 'https://www.smhi.se/rss/nyheter.xml' },
  ]},
];

function RSSForm({ content, onChange }) {
  const [q, setQ] = useState('');
  const [showCatalog, setShowCatalog] = useState(!content.url);

  const filtered = q.trim()
    ? RSS_CATALOG.flatMap(cat => cat.feeds.filter(f =>
        f.title.toLowerCase().includes(q.toLowerCase()) ||
        f.url.toLowerCase().includes(q.toLowerCase())
      )).map(f => ({ ...f, cat: RSS_CATALOG.find(c => c.feeds.includes(f))?.cat }))
    : null;

  const selectFeed = (feed) => {
    onChange({ ...content, url: feed.url, title: content.title || feed.title });
    setShowCatalog(false);
  };

  return (
    <Box sx={{ display:'flex', flexDirection:'column', gap: 2 }}>
      <TextField label="Titel (visas på skärmen)" value={content.title || ''} onChange={e => onChange({ ...content, title: e.target.value })} fullWidth size="small" />
      <TextField label="RSS-URL" value={content.url || ''} onChange={e => onChange({ ...content, url: e.target.value })} fullWidth size="small"
        InputProps={{ endAdornment: <Button size="small" onClick={() => setShowCatalog(v => !v)} sx={{ whiteSpace:'nowrap', color:'#FF6B00' }}>Välj flöde</Button> }} />

      {showCatalog && (
        <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 320, overflowY:'auto' }}>
          <TextField placeholder="Filtrera…" value={q} onChange={e => setQ(e.target.value)}
            size="small" fullWidth sx={{ mb: 1 }} />
          {(filtered || RSS_CATALOG.flatMap(c => c.feeds.map(f => ({ ...f, cat: c.cat })))).map((feed, i) => (
            <Box key={i} onClick={() => selectFeed(feed)}
              sx={{ px: 1.5, py: 1, cursor:'pointer', borderRadius: 1, '&:hover':{ bgcolor:'rgba(255,107,0,0.08)' } }}>
              <Typography variant="body2" fontWeight={600}>{feed.title}</Typography>
              <Typography variant="caption" sx={{ color:'#999' }}>{feed.cat} · {feed.url.replace(/https?:\/\//, '').substring(0, 50)}</Typography>
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
}

function UrlForm({ content, onChange }) {
  return (
    <TextField label="URL" value={content.url || ''} onChange={e => onChange({ ...content, url: e.target.value })} fullWidth
      helperText="Visas i iframe (funktionerar om sajten tillåter inbäddning)" />
  );
}

function WorldClockForm({ content, onChange }) {
  const zones = content.zones || [
    { label: 'Stockholm', tz: 'Europe/Stockholm' },
    { label: 'London', tz: 'Europe/London' },
    { label: 'New York', tz: 'America/New_York' },
    { label: 'Tokyo', tz: 'Asia/Tokyo' },
  ];
  const update = (i, field, val) => {
    const z = [...zones];
    z[i] = { ...z[i], [field]: val };
    onChange({ ...content, zones: z });
  };
  const addZone = () => onChange({ ...content, zones: [...zones, { label: '', tz: 'Europe/Stockholm' }] });
  const removeZone = (i) => onChange({ ...content, zones: zones.filter((_, j) => j !== i) });
  const clockType = content.clockType || 'digital';

  return (
    <Box sx={{ display:'flex', flexDirection:'column', gap: 2 }}>
      {/* Klocktyp */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Klocktyp</Typography>
        <Box sx={{ display:'flex', gap: 1.5 }}>
          {[
            { id:'digital',        label:'Digital',         preview:'12:34' },
            { id:'analog-modern',  label:'Analog – Modern', preview:'⊙' },
            { id:'analog-classic', label:'Analog – Klassisk', preview:'🕐' },
          ].map(ct => (
            <Box key={ct.id} onClick={() => onChange({ ...content, clockType: ct.id })}
              sx={{ flex: 1, border: clockType===ct.id ? '2px solid #FF6B00' : '2px solid #e0e0e0',
                borderRadius: 1.5, p: 1.5, textAlign:'center', cursor:'pointer',
                bgcolor: clockType===ct.id ? 'rgba(255,107,0,0.06)' : 'transparent',
                '&:hover':{ borderColor:'#FF6B00' } }}>
              <Typography sx={{ fontSize:'1.5rem', mb: 0.5 }}>{ct.preview}</Typography>
              <Typography variant="caption" fontWeight={clockType===ct.id ? 700 : 400}>{ct.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Divider />

      {/* Tidszoner */}
      {zones.map((z, i) => (
        <Box key={i} sx={{ display:'flex', gap: 1, alignItems:'flex-start' }}>
          <TextField label="Etikett" value={z.label} onChange={e => update(i,'label',e.target.value)} fullWidth size="small" />
          <TextField label="Tidszon (IANA)" value={z.tz} onChange={e => update(i,'tz',e.target.value)} fullWidth size="small"
            helperText={i===0?'t.ex. Europe/Stockholm':undefined} />
          <IconButton size="small" sx={{ mt: 0.5 }} onClick={() => removeZone(i)} disabled={zones.length <= 1}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Button startIcon={<AddIcon />} size="small" onClick={addZone} sx={{ color:'#FF6B00', alignSelf:'flex-start' }}>
        Lägg till tidszon
      </Button>
    </Box>
  );
}

function MenuForm({ content, onChange }) {
  const days = content.days || Array.from({ length: 5 }, (_, i) => ({
    label: ['Måndag','Tisdag','Onsdag','Torsdag','Fredag'][i], menu: ''
  }));
  const update = (i, val) => {
    const d = [...days]; d[i] = { ...d[i], menu: val };
    onChange({ ...content, days: d });
  };
  return (
    <Box sx={{ display:'flex', flexDirection:'column', gap: 2 }}>
      <TextField label="Rubriken" value={content.title || 'Veckans meny'} onChange={e => onChange({ ...content, title: e.target.value })} fullWidth />
      {days.map((day, i) => (
        <TextField key={i} label={day.label} value={day.menu} onChange={e => update(i, e.target.value)}
          fullWidth multiline rows={2} />
      ))}
    </Box>
  );
}

function ReferenceForm({ content, onChange }) {
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    getDocs(collection(db, 'referenceProjects')).then(snap => {
      setProjects(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, []);
  return (
    <Box sx={{ display:'flex', flexDirection:'column', gap: 2 }}>
      <TextField select label="Referensprojekt" value={content.projectId || ''} onChange={e => onChange({ ...content, projectId: e.target.value })} fullWidth>
        {projects.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
      </TextField>
      <TextField select label="Layout" value={content.layout || 'image-left'} onChange={e => onChange({ ...content, layout: e.target.value })} fullWidth>
        <MenuItem value="image-left">Bild vänster, text höger (standard)</MenuItem>
        <MenuItem value="image-right">Text vänster, bild höger</MenuItem>
        <MenuItem value="image-top">Bild övre halvan, text under</MenuItem>
        <MenuItem value="image-bg">Bild som bakgrund, text överlagrad</MenuItem>
        <MenuItem value="text-only">Bara text (utan bild)</MenuItem>
      </TextField>
      <TextField select label="Bildskala" value={content.imgFit || 'cover'} onChange={e => onChange({ ...content, imgFit: e.target.value })} fullWidth>
        <MenuItem value="cover">Fyll (beskär kanter)</MenuItem>
        <MenuItem value="contain">Passa in (visa hela bilden)</MenuItem>
      </TextField>
      <TextField select label="Textfärg" value={content.textColor || 'auto'} onChange={e => onChange({ ...content, textColor: e.target.value })} fullWidth>
        <MenuItem value="auto">Auto (vit på mörk, svart på ljus)</MenuItem>
        <MenuItem value="white">Alltid vit</MenuItem>
        <MenuItem value="dark">Alltid mörk</MenuItem>
      </TextField>
    </Box>
  );
}

function WinsForm({ content, onChange }) {
  return (
    <TextField select label="Period" value={content.period || ''} onChange={e => onChange({ ...content, period: e.target.value })} fullWidth>
      <MenuItem value="">Alla</MenuItem>
      <MenuItem value="month">Denna månad</MenuItem>
      <MenuItem value="quarter">Detta kvartal</MenuItem>
      <MenuItem value="year">I år</MenuItem>
    </TextField>
  );
}

function DateTearoffForm({ content, onChange }) {
  return (
    <Box sx={{ display:'flex', flexDirection:'column', gap: 2 }}>
      <Typography variant="subtitle2" color="text.secondary">
        Datumlapp visar dagens datum automatiskt — inget behöver konfigureras.
        Du kan anpassa utseendet nedan.
      </Typography>
      <TextField select label="Stil" value={content.style || 'classic'} onChange={e => onChange({ ...content, style: e.target.value })} fullWidth>
        <MenuItem value="classic">Klassisk (röd/vit, spiralbunden)</MenuItem>
        <MenuItem value="modern">Modern (svart/orange, minimalistisk)</MenuItem>
        <MenuItem value="vintage">Vintage (gulnad, gammal bok-känsla)</MenuItem>
        <MenuItem value="lingon">Lingonkalender (vit, lingonbär-bård)</MenuItem>
      </TextField>
      <TextField select label="Språk" value={content.lang || 'sv'} onChange={e => onChange({ ...content, lang: e.target.value })} fullWidth>
        <MenuItem value="sv">Svenska</MenuItem>
        <MenuItem value="en">English</MenuItem>
      </TextField>
      <TextField label="Undertitel (valfri, t.ex. veckonummer)" value={content.subtitle || ''}
        onChange={e => onChange({ ...content, subtitle: e.target.value })} fullWidth size="small"
        placeholder="Lämna tom för veckodagen automatiskt" />
    </Box>
  );
}

function EmojiForm({ content, onChange }) {
  return (
    <Box sx={{ display:'flex', flexDirection:'column', gap: 2 }}>
      <TextField
        label="Emoji"
        value={content.emoji || ''}
        onChange={e => onChange({ ...content, emoji: e.target.value })}
        fullWidth
        inputProps={{ style: { fontSize: '2rem' } }}
        helperText="Klistra in eller skriv en emoji, t.ex. 🎉 🏆 🔥"
      />
      <TextField
        label="Bildtext (valfri)"
        value={content.caption || ''}
        onChange={e => onChange({ ...content, caption: e.target.value })}
        fullWidth
      />
      <TextField select label="Storlek" value={content.size || 'xl'} onChange={e => onChange({ ...content, size: e.target.value })} fullWidth>
        <MenuItem value="lg">Stor</MenuItem>
        <MenuItem value="xl">Mycket stor (standard)</MenuItem>
        <MenuItem value="xxl">Enorm</MenuItem>
      </TextField>
      <TextField select label="Bakgrund" value={content.bgPresetId || 'grad-night'} onChange={e => onChange({ ...content, bgPresetId: e.target.value })} fullWidth>
        {PRESET_BACKGROUNDS.map(b => <MenuItem key={b.id} value={b.id}>{b.label}</MenuItem>)}
      </TextField>
    </Box>
  );
}

function EmbedUrlForm({ content, onChange, placeholder, helpText, helpItems }) {
  return (
    <Box sx={{ display:'flex', flexDirection:'column', gap: 2 }}>
      <TextField
        label="Embed-URL"
        value={content.embedUrl || ''}
        onChange={e => onChange({ ...content, embedUrl: e.target.value })}
        fullWidth
        placeholder={placeholder || 'https://...'}
        helperText="Klistra in hela iframe-URL:en (src-attributet)"
      />
      {helpText && (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fffbf5' }}>
          <Typography variant="subtitle2" sx={{ color:'#FF6B00', mb: 1 }}>Hur du hittar URL:en</Typography>
          {helpItems ? (
            <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
              {helpItems.map((s, i) => <Typography key={i} component="li" variant="body2" sx={{ mb: 0.5 }}>{s}</Typography>)}
            </Box>
          ) : (
            <Typography variant="body2">{helpText}</Typography>
          )}
        </Paper>
      )}
    </Box>
  );
}

function FacebookForm({ content, onChange }) {
  return (
    <Box sx={{ display:'flex', flexDirection:'column', gap: 2 }}>
      <TextField
        label="Facebook-sidans URL"
        value={content.pageUrl || ''}
        onChange={e => onChange({ ...content, pageUrl: e.target.value })}
        fullWidth
        placeholder="https://www.facebook.com/dinsida"
        helperText="URL till din Facebook-sida (inte profil eller grupp)"
      />
      <TextField select label="Fliken som visas" value={content.tabs || 'timeline'} onChange={e => onChange({ ...content, tabs: e.target.value })} fullWidth>
        <MenuItem value="timeline">Inlägg (timeline)</MenuItem>
        <MenuItem value="events">Evenemang</MenuItem>
      </TextField>
      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fffbf5' }}>
        <Typography variant="subtitle2" sx={{ color:'#FF6B00', mb: 1 }}>Obs!</Typography>
        <Typography variant="body2">Visar enbart inlägg från Facebook-sidor (inte profiler eller grupper). Videoklipp, reels och event visas inte.</Typography>
      </Paper>
    </Box>
  );
}

function extractInstagramUsername(raw) {
  const trimmed = (raw || '').trim().replace(/^@/, '').replace(/\/$/, '');
  const m = trimmed.match(/instagram\.com\/([^/?#\s]+)/);
  return m ? m[1] : trimmed;
}

function InstagramForm({ content, onChange }) {
  return (
    <Box sx={{ display:'flex', flexDirection:'column', gap: 2 }}>
      <TextField
        label="Instagram-användarnamn eller URL"
        value={content.username || ''}
        onChange={e => onChange({ ...content, username: extractInstagramUsername(e.target.value) })}
        fullWidth
        placeholder="tikabsverige  eller  https://www.instagram.com/tikabsverige/"
        helperText="Klistra in hela Instagram-URL:en eller bara @-handtaget — @ tas bort automatiskt"
      />
      <TextField select label="Visningsläge" value={content.displayMode || 'grid'} onChange={e => onChange({ ...content, displayMode: e.target.value })} fullWidth>
        <MenuItem value="grid">Bildrutnät (senaste 6 bilder)</MenuItem>
        <MenuItem value="latest">Senaste bilden stor</MenuItem>
      </TextField>
      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fffbf5' }}>
        <Typography variant="subtitle2" sx={{ color:'#FF6B00', mb: 1 }}>Krav</Typography>
        <Typography variant="body2">Kontot måste vara ett publikt Instagram Business- eller Creator-konto. Reels visas inte. Uppdateras automatiskt.</Typography>
      </Paper>
    </Box>
  );
}

function extractLinkedInSlug(raw) {
  const trimmed = (raw || '').trim().replace(/\/$/, '');
  const m = trimmed.match(/linkedin\.com\/company\/([^/?#\s]+)/);
  return m ? m[1] : trimmed;
}

function LinkedInForm({ content, onChange }) {
  return (
    <Box sx={{ display:'flex', flexDirection:'column', gap: 2 }}>
      <TextField
        label="LinkedIn-sida URL eller slug"
        value={content.companySlug || ''}
        onChange={e => onChange({ ...content, companySlug: extractLinkedInSlug(e.target.value) })}
        fullWidth
        placeholder="tikab-strukturmekanik-ab  eller  https://www.linkedin.com/company/tikab-strukturmekanik-ab/"
        helperText="Klistra in hela LinkedIn-URL:en — sluggen extraheras automatiskt"
      />
      <TextField select label="Antal inlägg" value={content.count || 5} onChange={e => onChange({ ...content, count: Number(e.target.value) })} fullWidth>
        <MenuItem value={3}>3 inlägg</MenuItem>
        <MenuItem value={5}>5 inlägg (standard)</MenuItem>
        <MenuItem value={8}>8 inlägg</MenuItem>
      </TextField>
      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fffbf5' }}>
        <Typography variant="subtitle2" sx={{ color:'#FF6B00', mb: 1 }}>Obs!</Typography>
        <Typography variant="body2">Kräver en publik LinkedIn-företagssida. Hämtar automatiskt de senaste inläggen.</Typography>
      </Paper>
    </Box>
  );
}

function QRDisplayForm({ content, onChange }) {
  const c = content || {};
  return (
    <Box sx={{ display:'flex', flexDirection:'column', gap: 2 }}>
      <Alert severity="info" sx={{ fontSize:'0.82rem' }}>
        Sliden visar en QR-kod som pekar på den aktuella skärmens URL.
        Besökare skannar koden och öppnar samma bildspel på sin mobil.
        Logotypen från företagsinställningarna placeras automatiskt i mitten av koden.
      </Alert>
      <TextField fullWidth label="Rubrik" value={c.heading ?? 'Se bildspelet i din mobil'}
        onChange={e => onChange({ ...c, heading: e.target.value })}
        helperText="Visas ovanför QR-koden" />
      <TextField fullWidth label="Instruktion" value={c.instruction ?? 'Skanna QR-koden med kameran'}
        onChange={e => onChange({ ...c, instruction: e.target.value })}
        helperText="Visas under QR-koden" />

      <Typography variant="subtitle2" sx={{ mt: 1 }}>Bakgrund</Typography>
      <Box sx={{ display:'flex', flexWrap:'wrap', gap: 1 }}>
        {PRESET_BACKGROUNDS.map(b => (
          <Box key={b.id} onClick={() => onChange({ ...c, bgPresetId: b.id, bgColor: '' })}
            sx={{ width: 48, height: 32, borderRadius: 1, background: b.css, cursor:'pointer',
              border: c.bgPresetId === b.id ? '3px solid #FF6B00' : '2px solid transparent',
              transition: 'border 0.15s' }}>
            <Typography sx={{ fontSize:'0.55rem', color:'rgba(255,255,255,0.7)', textAlign:'center', lineHeight:'32px', userSelect:'none' }}>{b.label}</Typography>
          </Box>
        ))}
        <Box onClick={() => onChange({ ...c, bgPresetId: '', bgColor: c.bgColor || '#1a1a1a' })}
          sx={{ width: 48, height: 32, borderRadius: 1, bgcolor: c.bgColor || '#1a1a1a', cursor:'pointer',
            border: !c.bgPresetId ? '3px solid #FF6B00' : '2px solid #555', display:'flex', alignItems:'center', justifyContent:'center' }}>
          <Typography sx={{ fontSize:'0.55rem', color:'#fff' }}>Egen</Typography>
        </Box>
      </Box>
      {!c.bgPresetId && (
        <TextField fullWidth size="small" label="Bakgrundsfärg (hex)" value={c.bgColor ?? '#1a1a1a'}
          onChange={e => onChange({ ...c, bgColor: e.target.value })} />
      )}

      <Box sx={{ display:'flex', gap: 2 }}>
        <TextField fullWidth size="small" label="Textfärg" value={c.textColor ?? '#ffffff'}
          onChange={e => onChange({ ...c, textColor: e.target.value })} />
        <TextField fullWidth size="small" label="Accentfärg (glöd runt QR)" value={c.accentColor ?? '#FF6B00'}
          onChange={e => onChange({ ...c, accentColor: e.target.value })} />
      </Box>
    </Box>
  );
}

function FormForType({ type, content, onChange }) {
  switch(type) {
    case 'text': return <TextForm content={content} onChange={onChange} />;
    case 'album': return <AlbumForm content={content} onChange={onChange} />;
    case 'media': return <MediaForm content={content} onChange={onChange} />;
    case 'image_url': return <ImageUrlForm content={content} onChange={onChange} />;
    case 'video': return <VideoForm content={content} onChange={onChange} />;
    case 'weather': return <WeatherForm content={content} onChange={onChange} />;
    case 'weather_map': return <WeatherMapForm content={content} onChange={onChange} />;
    case 'traffic': return <TrafficForm content={content} onChange={onChange} />;
    case 'rss': return <RSSForm content={content} onChange={onChange} />;
    case 'url':
    case 'calendar': return <UrlForm content={content} onChange={onChange} />;
    case 'world_clock': return <WorldClockForm content={content} onChange={onChange} />;
    case 'menu': return <MenuForm content={content} onChange={onChange} />;
    case 'lagbasn_reference': return <ReferenceForm content={content} onChange={onChange} />;
    case 'lagbasn_leads': return <Box><Typography sx={{ color:'#666' }}>Hämtar automatiskt aktiva anbud (status: active eller submitted).</Typography></Box>;
    case 'lagbasn_wins': return <WinsForm content={content} onChange={onChange} />;
    case 'date_tearoff': return <DateTearoffForm content={content} onChange={onChange} />;
    case 'emoji': return <EmojiForm content={content} onChange={onChange} />;
    case 'google_calendar': return <EmbedUrlForm content={content} onChange={onChange}
      placeholder="https://calendar.google.com/calendar/embed?src=..."
      helpItems={[
        'Öppna Google Kalender → Inställningar (kugghjulet)',
        'Klicka på din kalender i vänsterkolumnen',
        'Scrolla ned till "Integrera kalender"',
        'Kopiera länken under "Embed this calendar" (iframe src-attributet)',
      ]} />;
    case 'ms_calendar': return <EmbedUrlForm content={content} onChange={onChange}
      placeholder="https://outlook.office.com/calendar/published/..."
      helpItems={[
        'Öppna Outlook → Kalender',
        'Högerklicka på kalendern → Dela → Publicera online',
        'Välj "Visa tillgänglighet" eller detaljnivå',
        'Kopiera HTML-länken (iframe src-attributet)',
      ]} />;
    case 'power_bi': return <EmbedUrlForm content={content} onChange={onChange}
      placeholder="https://app.powerbi.com/view?r=..."
      helpItems={[
        'Öppna din rapport i Power BI',
        'Klicka på Arkiv → Publicera på webben',
        'Välj storlek och klicka "Skapa inbäddningskod"',
        'Kopiera src-URL:en från iframe-koden',
      ]} />;
    case 'sharepoint': return <EmbedUrlForm content={content} onChange={onChange}
      placeholder="https://dittforetag.sharepoint.com/..."
      helpItems={[
        'Öppna SharePoint-sidan du vill visa',
        'Kopiera URL:en direkt från webbläsarens adressfält',
        'OBS: Skärmen måste vara inloggad på samma konto för att se innehållet',
      ]} />;
    case 'social_facebook': return <FacebookForm content={content} onChange={onChange} />;
    case 'social_instagram': return <InstagramForm content={content} onChange={onChange} />;
    case 'social_linkedin': return <LinkedInForm content={content} onChange={onChange} />;
    case 'qr_display': return <QRDisplayForm content={content} onChange={onChange} />;
    case 'birthday': return (
      <Box sx={{ display:'flex', flexDirection:'column', gap: 2 }}>
        <Alert severity="info" sx={{ fontSize:'0.82rem' }}>
          Sliden visas automatiskt den dag en medarbetare fyller år och hoppas över alla andra dagar.
          Medarbetarnas foto och födelsedata hämtas från personalregistret.
        </Alert>
        <TextField fullWidth label="Hälsningsfras" value={content?.greeting ?? 'Grattis på födelsedagen!'}
          onChange={e => onChange({ ...content, greeting: e.target.value })}
          helperText="Visas som citat under namnet" />
        <TextField fullWidth label="Bakgrund (CSS gradient eller färg)" value={content?.bgColor ?? 'linear-gradient(135deg,#7b1fa2 0%,#e91e63 100%)'}
          onChange={e => onChange({ ...content, bgColor: e.target.value })}
          helperText="T.ex. linear-gradient(135deg,#7b1fa2 0%,#e91e63 100%) eller #1a1a1a" />
        <TextField fullWidth label="Textfärg" value={content?.textColor ?? '#ffffff'}
          onChange={e => onChange({ ...content, textColor: e.target.value })} />
      </Box>
    );
    default: return <Typography>Okänd typ: {type}</Typography>;
  }
}

export default function CastNSlideEditor() {
  const { castId } = useParams();
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};

  const slideType = state.slideType || 'text';
  const slideId = state.slideId || null; // editing existing
  const companyId = userProfile?.companyId || '';
  const uid = currentUser?.uid || '';
  const displayName = userProfile?.name || userProfile?.displayName || currentUser?.email || '';

  const [title, setTitle] = useState('');
  const [content, setContent] = useState({});
  const [duration, setDuration] = useState(10);
  const [zone, setZone] = useState('HUVUDANSLAG');
  const [schedule, setSchedule] = useState({ enabled: false, startDate: '', endDate: '', days: [1,2,3,4,5], startTime: '', endTime: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load existing slide if editing
  useEffect(() => {
    if (!slideId) return;
    getDoc(doc(db, 'slides', slideId)).then(snap => {
      if (snap.exists()) {
        const d = snap.data();
        setTitle(d.title || '');
        setContent(d.content || {});
        setZone(d.zone || 'HUVUDANSLAG');
        if (d.schedule) setSchedule(d.schedule);
      }
    });
    // Also get duration from cast
    if (castId) {
      getDoc(doc(db, 'casts', castId)).then(snap => {
        if (snap.exists()) {
          const sr = (snap.data().slides || []).find(s => s.slideId === slideId);
          if (sr) setDuration(sr.duration || 10);
        }
      });
    }
  }, [slideId, castId]);

  function stripUndefined(obj) {
    if (obj === null || obj === undefined) return null;
    if (typeof obj !== 'object' || Array.isArray(obj)) return obj;
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, typeof v === 'object' && v !== null && !Array.isArray(v) ? stripUndefined(v) : v])
    );
  }

  async function handleSave() {
    setSaving(true); setError(''); setSuccess('');
    try {
      let sid = slideId;
      const slideData = {
        type: slideType,
        title,
        content: stripUndefined(content) || {},
        zone,
        schedule: schedule.enabled ? schedule : { enabled: false },
        companyId,
        updatedAt: serverTimestamp(),
      };
      if (!slideId) {
        // Create new slide
        slideData.createdBy = uid;
        slideData.createdByName = displayName;
        slideData.createdAt = serverTimestamp();
        const ref = await addDoc(collection(db, 'slides'), slideData);
        sid = ref.id;
        // Add to cast
        if (castId) {
          const castSnap = await getDoc(doc(db, 'casts', castId));
          if (castSnap.exists()) {
            const existing = castSnap.data().slides || [];
            await updateDoc(doc(db, 'casts', castId), {
              slides: [...existing, { slideId: sid, duration, zone }],
            });
          }
        }
      } else {
        // Update existing slide
        await updateDoc(doc(db, 'slides', slideId), slideData);
        // Update duration/zone in cast
        if (castId) {
          const castSnap = await getDoc(doc(db, 'casts', castId));
          if (castSnap.exists()) {
            const updated = (castSnap.data().slides || []).map(s =>
              s.slideId === slideId ? { ...s, duration, zone } : s
            );
            await updateDoc(doc(db, 'casts', castId), { slides: updated });
          }
        }
      }
      setSuccess('Anslag sparat!');
      setTimeout(() => navigate(`/marketing/castn/casts`), 1000);
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <Box sx={{ minHeight:'100vh', bgcolor:'#f5f5f5' }}>
      {/* Header */}
      <Box sx={{ bgcolor:'#1a1a1a', color:'white', px: 3, py: 2, display:'flex', alignItems:'center', gap: 2 }}>
        <LagbasLogo size={32} />
        <Typography variant="h6" fontWeight="bold">
          Cast<span style={{ color:'#FF6B00' }}>N</span>
          <span style={{ color:'#aaa', fontWeight:400, fontSize:'0.9rem', ml: 1 }}>– {slideId ? 'Redigera' : 'Nytt'} anslag</span>
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<ArrowBackIcon />} sx={{ color:'#aaa' }} onClick={() => navigate(-1)}>
          Tillbaka
        </Button>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display:'flex', gap: 3 }}>
          {/* Form panel */}
          <Box sx={{ flex: 1 }}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display:'flex', alignItems:'center', gap: 2, mb: 3 }}>
                <Chip label={TYPE_LABELS[slideType] || slideType} sx={{ bgcolor:'#FF6B00', color:'white', fontWeight:'bold' }} />
              </Box>

              <Box sx={{ display:'flex', flexDirection:'column', gap: 3 }}>
                <TextField label="Titel (intern)" value={title} onChange={e => setTitle(e.target.value)} fullWidth />

                <Divider />

                <FormForType type={slideType} content={content} onChange={setContent} />

                <Divider />

                <Box sx={{ display:'flex', gap: 2 }}>
                  <TextField label="Visningstid (sekunder)" type="number" value={duration}
                    onChange={e => setDuration(Math.max(1, parseInt(e.target.value)||10))}
                    inputProps={{ min: 1, max: 3600 }} sx={{ width: 200 }} />
                  <TextField select label="Zon" value={zone} onChange={e => setZone(e.target.value)} sx={{ width: 240 }}>
                    {ZONE_OPTIONS.map(z => <MenuItem key={z.value} value={z.value}>{z.label}</MenuItem>)}
                  </TextField>
                </Box>

                {/* ── Smart schemaläggning ── */}
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={schedule.enabled}
                        onChange={e => setSchedule(s => ({ ...s, enabled: e.target.checked }))}
                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color:'#FF6B00' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor:'#FF6B00' } }}
                      />
                    }
                    label={<Typography fontWeight="bold">Smart schemaläggning</Typography>}
                  />
                  <Collapse in={schedule.enabled}>
                    <Box sx={{ display:'flex', flexDirection:'column', gap: 2, mt: 2 }}>
                      {/* Date range */}
                      <Box sx={{ display:'flex', gap: 2 }}>
                        <TextField label="Startdatum" type="date" value={schedule.startDate || ''}
                          onChange={e => setSchedule(s => ({ ...s, startDate: e.target.value }))}
                          InputLabelProps={{ shrink: true }} sx={{ flex: 1 }}
                          helperText="Lämna tom = alltid från idag" />
                        <TextField label="Slutdatum" type="date" value={schedule.endDate || ''}
                          onChange={e => setSchedule(s => ({ ...s, endDate: e.target.value }))}
                          InputLabelProps={{ shrink: true }} sx={{ flex: 1 }}
                          helperText="Lämna tom = ingen slutgräns" />
                      </Box>
                      {/* Days of week */}
                      <Box>
                        <Typography variant="caption" sx={{ color:'#666', display:'block', mb: 1 }}>Veckodagar</Typography>
                        <ToggleButtonGroup
                          value={schedule.days || []}
                          onChange={(_, val) => setSchedule(s => ({ ...s, days: val }))}
                          size="small"
                          sx={{ flexWrap:'wrap', gap: 0.5 }}
                        >
                          {['Mån','Tis','Ons','Tor','Fre','Lör','Sön'].map((d, i) => (
                            <ToggleButton key={i} value={i === 6 ? 0 : i + 1}
                              sx={{ '&.Mui-selected':{ bgcolor:'#FF6B00', color:'white', '&:hover':{ bgcolor:'#e55a00' } } }}>
                              {d}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      </Box>
                      {/* Time range */}
                      <Box sx={{ display:'flex', gap: 2 }}>
                        <TextField label="Starttid" type="time" value={schedule.startTime || ''}
                          onChange={e => setSchedule(s => ({ ...s, startTime: e.target.value }))}
                          InputLabelProps={{ shrink: true }} sx={{ flex: 1 }}
                          helperText="Lämna tom = hela dygnet" />
                        <TextField label="Sluttid" type="time" value={schedule.endTime || ''}
                          onChange={e => setSchedule(s => ({ ...s, endTime: e.target.value }))}
                          InputLabelProps={{ shrink: true }} sx={{ flex: 1 }}
                          helperText="Lämna tom = hela dygnet" />
                      </Box>
                    </Box>
                  </Collapse>
                </Paper>

                {error && <Alert severity="error">{error}</Alert>}
                {success && <Alert severity="success">{success}</Alert>}

                <Button variant="contained" startIcon={saving ? <CircularProgress size={16} sx={{ color:'white' }} /> : <SaveIcon />}
                  onClick={handleSave} disabled={saving}
                  sx={{ bgcolor:'#FF6B00', '&:hover':{ bgcolor:'#e55a00' }, alignSelf:'flex-start', px: 4, py: 1.5 }}>
                  {saving ? 'Sparar…' : 'Spara anslag'}
                </Button>
              </Box>
            </Paper>
          </Box>

          {/* Preview panel */}
          <Box sx={{ width: 400, flexShrink: 0 }}>
            <Paper sx={{ p: 2, position:'sticky', top: 80 }}>
              <Typography variant="overline" sx={{ color:'#FF6B00', fontWeight:'bold' }}>Förhandsgranskning</Typography>
              <Box sx={{ mt: 1, bgcolor:'#111', borderRadius: 1, aspectRatio:'16/9', overflow:'hidden',
                position:'relative' }}>
                <SlideRenderer slide={{ type: slideType, content }} />
              </Box>
              <Box sx={{ mt: 1, display:'flex', gap: 1, flexWrap:'wrap' }}>
                <Chip label={`${duration}s`} size="small" variant="outlined" />
                <Chip label={ZONE_OPTIONS.find(z=>z.value===zone)?.label || zone} size="small"
                  sx={{ bgcolor: zone==='HUVUDANSLAG'?'#FF6B00':zone.startsWith('SIDO')?'#2196F3':'#4caf50', color:'white' }} />
              </Box>
            </Paper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
